import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  ChartBarIcon, 
  DocumentTextIcon, 
  UserIcon, 
  ArrowDownTrayIcon,
  FunnelIcon,
  CalendarIcon,
  MapPinIcon,
  AcademicCapIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

const Reports = () => {
  const { authAxios } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false); // 필터 패널 기본 숨김
  
  // 상태 관리
  const [activeTab, setActiveTab] = useState('individual'); // comprehensive, daily, individual
  const [reportData, setReportData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [filters, setFilters] = useState({
    educationType: '', // 단기/중기/장기
    region: '', // 압해/안좌 등
    term: '', // 기수
    startDate: '',
    endDate: '',
    className: '',
    instructorName: '',
    studentName: '',
    studentPhone: ''
  });
  
  // 필터 옵션 데이터
  const [filterOptions, setFilterOptions] = useState({
    educationTypes: [],
    regions: [],
    terms: [],
    classes: [],
    instructors: [],
    students: []
  });

  // 보고서 탭 정의
  const reportTabs = [
    {
      id: 'individual',
      name: '출결 이력 리포트',
      icon: UserIcon,
      description: '개별 교육생 이수 현황'
    },
    {
      id: 'comprehensive',
      name: '출결 종합 리포트',
      icon: ChartBarIcon,
      description: '교육구분별/기수별 종합 통계'
    },
    {
      id: 'daily',
      name: '일자별 출결 상세',
      icon: DocumentTextIcon,
      description: '강의별 상세 출결 현황'
    }
  ];

  // 필터 옵션 로드
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [educationTypesRes, regionsRes, termsRes, classesRes, instructorsRes, studentsRes] = await Promise.all([
          authAxios.get('/reports/filter-options/education-types'),
          authAxios.get('/reports/filter-options/regions'),
          authAxios.get('/reports/filter-options/terms'),
          authAxios.get('/reports/filter-options/classes'),
          authAxios.get('/reports/filter-options/instructors'),
          authAxios.get('/reports/filter-options/students')
        ]);

        setFilterOptions({
          educationTypes: educationTypesRes.data || [],
          regions: regionsRes.data || [],
          terms: termsRes.data || [],
          classes: classesRes.data || [],
          instructors: instructorsRes.data || [],
          students: studentsRes.data || []
        });
      } catch (err) {
        console.error('필터 옵션 로드 오류:', err);
        setError('필터 옵션을 불러오는 중 오류가 발생했습니다.');
      }
    };

    loadFilterOptions();
  }, []);

  // 보고서 데이터 조회
  const fetchReportData = async () => {
    if (!activeTab) {
      console.warn('activeTab이 설정되지 않았습니다.');
      return;
    }

    // 유효한 탭인지 확인
    const validTabs = ['comprehensive', 'daily', 'individual'];
    if (!validTabs.includes(activeTab)) {
      setError('올바르지 않은 보고서 유형입니다.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.toString().trim()) {
          params.append(key, value.toString().trim());
        }
      });

      // 디버깅: 전송되는 필터 파라미터 로그
      console.log('일자별 리포트 요청 필터:', params.toString());
      console.log('원본 필터 객체:', filters);

      const response = await authAxios.get(`/reports/${activeTab}?${params.toString()}`);
      console.log(`/reports/${activeTab}?${params.toString()}`);
      console.log('일자별 리포트 응답:', response.data);
      // 응답 데이터 검증
      if (response.data !== null && response.data !== undefined) {
        setReportData(response.data);
      } else {
        setReportData(null);
        setError('보고서 데이터가 없습니다.');
      }
    } catch (err) {
      console.error('보고서 데이터 로드 오류:', err);
      
      // 더 구체적인 오류 메시지
      let errorMessage = '보고서 데이터를 불러오는 중 오류가 발생했습니다.';
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = '요청한 보고서를 찾을 수 없습니다.';
        } else if (err.response.status === 500) {
          errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        } else if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.code === 'NETWORK_ERROR') {
        errorMessage = '네트워크 연결을 확인해주세요.';
      }
      
      setError(errorMessage);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // 필터 변경 핸들러
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 필터 초기화
  const resetFilters = () => {
    setFilters({
      educationType: '',
      region: '',
      term: '',
      startDate: '',
      endDate: '',
      className: '',
      instructorName: '',
      studentName: '',
      studentPhone: ''
    });
  };

  // Excel 다운로드
  const downloadExcel = async () => {
    if (!activeTab) {
      setError('보고서 유형을 선택해주세요.');
      return;
    }

    // 유효한 탭인지 확인
    const validTabs = ['comprehensive', 'daily', 'individual'];
    if (!validTabs.includes(activeTab)) {
      setError('올바르지 않은 보고서 유형입니다.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.toString().trim()) {
          params.append(key, value.toString().trim());
        }
      });

      const response = await authAxios.get(`/reports/${activeTab}/excel?${params.toString()}`, {
        responseType: 'blob'
      });

      // 응답 검증
      if (!response.data || response.data.size === 0) {
        throw new Error('Excel 파일이 생성되지 않았습니다.');
      }

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const reportName = reportTabs.find(tab => tab.id === activeTab)?.name || '보고서';
      const fileName = `${reportName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.download = fileName;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel 다운로드 오류:', err);
      
      let errorMessage = 'Excel 다운로드 중 오류가 발생했습니다.';
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = '다운로드할 보고서를 찾을 수 없습니다.';
        } else if (err.response.status === 500) {
          errorMessage = 'Excel 생성 중 서버 오류가 발생했습니다.';
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // 탭 변경 시 데이터 로드
  useEffect(() => {
    if (activeTab) {
      fetchReportData();
    }
  }, [activeTab]);

  // 프로그램 클릭 핸들러
  const handleProgramClick = async (structure, module, program) => {
    try {
      setModalLoading(true);
      setShowModal(true);
      
      const response = await authAxios.get(`/reports/program-classes`, {
        params: { structure, module, program }
      });
      
      setModalData(response.data);
    } catch (err) {
      console.error('프로그램별 강의 조회 오류:', err);
      setError('강의 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setModalLoading(false);
    }
  };

  // 모달 닫기
  const closeModal = () => {
    setShowModal(false);
    setModalData(null);
  };

  // 필터 렌더링
  const renderFilters = () => {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex items-center mb-4">
          <FunnelIcon className="w-5 h-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">필터 조건</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* 공통 필터 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              교육구분
            </label>
            <select
              value={filters.educationType}
              onChange={(e) => handleFilterChange('educationType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">전체</option>
              {filterOptions.educationTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              구성
            </label>
            <select
              value={filters.region}
              onChange={(e) => handleFilterChange('region', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">전체</option>
              {filterOptions.regions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              모듈
            </label>
            <select
              value={filters.term}
              onChange={(e) => handleFilterChange('term', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">전체</option>
              {filterOptions.terms.map(term => (
                <option key={term} value={term}>{term}</option>
              ))}
            </select>
          </div>

          {/* 일자별 상세 리포트용 추가 필터 */}
          {activeTab === 'daily' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  시작일
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  종료일
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  강의명
                </label>
                <select
                  value={filters.className}
                  onChange={(e) => handleFilterChange('className', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">전체</option>
                  {filterOptions.classes.map(cls => (
                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  강사명
                </label>
                <select
                  value={filters.instructorName}
                  onChange={(e) => handleFilterChange('instructorName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">전체</option>
                  {filterOptions.instructors.map(instructor => (
                    <option key={instructor.id} value={instructor.id}>{instructor.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* 개별 이수 리포트용 필터 */}
          {activeTab === 'individual' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  교육생 이름
                </label>
                <input
                  type="text"
                  value={filters.studentName}
                  onChange={(e) => handleFilterChange('studentName', e.target.value)}
                  placeholder="교육생 이름 검색"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  연락처
                </label>
                <input
                  type="text"
                  value={filters.studentPhone}
                  onChange={(e) => handleFilterChange('studentPhone', e.target.value)}
                  placeholder="연락처 검색"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={resetFilters}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            필터 초기화
          </button>
          
          <button
            onClick={fetchReportData}
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isLoading ? '조회 중...' : '조회'}
          </button>
        </div>
      </div>
    );
  };

  // 종합 리포트 렌더링 - 새로운 테이블 구조
  const renderComprehensiveReport = () => {
    if (!reportData) return null;

    console.log('리포트 데이터:', reportData);

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">출결 종합 리포트</h3>
          <p className="text-sm text-gray-600 mt-1">구성별/모듈별/프로그램별 종합 통계</p>
        </div>
        
        <div className="p-6">
          {/* 요약 통계 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <AcademicCapIcon className="w-8 h-8 text-blue-600" />
                <div className="ml-3">
                  <p className="text-sm text-blue-600">총 수강생</p>
                  <p className="text-2xl font-bold text-blue-900">{reportData.summary?.totalStudents || 0}명</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <ChartBarIcon className="w-8 h-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-sm text-green-600">평균 출석률</p>
                  <p className="text-2xl font-bold text-green-900">
                    {reportData.summary?.averageAttendance || 0}%
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center">
                <DocumentTextIcon className="w-8 h-8 text-orange-600" />
                <div className="ml-3">
                  <p className="text-sm text-orange-600">총 강의 수</p>
                  <p className="text-2xl font-bold text-orange-900">{reportData.summary?.totalClasses || 0}개</p>
                </div>
              </div>
            </div>
          </div>

          {/* 새로운 테이블 구조 */}
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  {/* 첫 번째 행: 메인 헤더 */}
                  <th rowSpan="2" className="border border-gray-300 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    구성
                  </th>
                  <th rowSpan="2" className="border border-gray-300 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    모듈
                  </th>
                  <th rowSpan="2" className="border border-gray-300 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    프로그램
                  </th>
                  <th colSpan="6" className="border border-gray-300 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                    단기
                  </th>
                  <th colSpan="6" className="border border-gray-300 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50">
                    중기
                  </th>
                  <th colSpan="6" className="border border-gray-300 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-yellow-50">
                    장기
                  </th>
                </tr>
                <tr>
                  {/* 두 번째 행: 서브 헤더 */}
                  {['단기', '중기', '장기'].map((period, periodIndex) => (
                    <React.Fragment key={period}>
                      <th className={`border border-gray-300 px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase ${
                        periodIndex === 0 ? 'bg-blue-50' : periodIndex === 1 ? 'bg-green-50' : 'bg-yellow-50'
                      }`}>
                        교육기간(일)
                      </th>
                      <th className={`border border-gray-300 px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase ${
                        periodIndex === 0 ? 'bg-blue-50' : periodIndex === 1 ? 'bg-green-50' : 'bg-yellow-50'
                      }`}>
                        교육장소(개소)
                      </th>
                      <th className={`border border-gray-300 px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase ${
                        periodIndex === 0 ? 'bg-blue-50' : periodIndex === 1 ? 'bg-green-50' : 'bg-yellow-50'
                      }`}>
                        강의수
                      </th>
                      <th className={`border border-gray-300 px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase ${
                        periodIndex === 0 ? 'bg-blue-50' : periodIndex === 1 ? 'bg-green-50' : 'bg-yellow-50'
                      }`}>
                        강사수
                      </th>
                      <th className={`border border-gray-300 px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase ${
                        periodIndex === 0 ? 'bg-blue-50' : periodIndex === 1 ? 'bg-green-50' : 'bg-yellow-50'
                      }`}>
                        교육생수
                      </th>
                      <th className={`border border-gray-300 px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase ${
                        periodIndex === 0 ? 'bg-blue-50' : periodIndex === 1 ? 'bg-green-50' : 'bg-yellow-50'
                      }`}>
                        출석률
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  // rowSpan을 위한 데이터 분석 - 연속된 같은 값들만 그룹핑
                  const rowSpanInfo = {};
                  
                  reportData.details?.forEach((item, index) => {
                    // 구성 rowSpan 계산
                    const structureKey = `structure-${index}`;
                    if (index === 0 || reportData.details[index - 1].structure !== item.structure) {
                      // 새로운 구성 시작
                      let count = 1;
                      for (let i = index + 1; i < reportData.details.length; i++) {
                        if (reportData.details[i].structure === item.structure) {
                          count++;
                        } else {
                          break;
                        }
                      }
                      rowSpanInfo[structureKey] = { show: true, span: count };
                    } else {
                      rowSpanInfo[structureKey] = { show: false, span: 0 };
                    }

                    // 모듈 rowSpan 계산
                    const moduleKey = `module-${index}`;
                    if (index === 0 || 
                        reportData.details[index - 1].structure !== item.structure ||
                        reportData.details[index - 1].module !== item.module) {
                      // 새로운 모듈 시작
                      let count = 1;
                      for (let i = index + 1; i < reportData.details.length; i++) {
                        if (reportData.details[i].structure === item.structure && 
                            reportData.details[i].module === item.module) {
                          count++;
                        } else {
                          break;
                        }
                      }
                      rowSpanInfo[moduleKey] = { show: true, span: count };
                    } else {
                      rowSpanInfo[moduleKey] = { show: false, span: 0 };
                    }
                  });

                  return reportData.details?.map((item, index) => {
                    const structureKey = `structure-${index}`;
                    const moduleKey = `module-${index}`;

                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        {/* 구성 - rowSpan 적용 */}
                        {rowSpanInfo[structureKey]?.show && (
                          <td 
                            rowSpan={rowSpanInfo[structureKey].span}
                            className="border border-gray-300 px-4 py-3 text-sm font-medium text-gray-900 text-center align-middle"
                          >
                            {item.structure}
                          </td>
                        )}
                        
                        {/* 모듈 - rowSpan 적용 */}
                        {rowSpanInfo[moduleKey]?.show && (
                          <td 
                            rowSpan={rowSpanInfo[moduleKey].span}
                            className="border border-gray-300 px-4 py-3 text-sm text-gray-500 text-center align-middle"
                          >
                            {item.module}
                          </td>
                        )}
                        
                        {/* 프로그램 - 클릭 가능 */}
                        <td 
                          className="border border-gray-300 px-4 py-3 text-sm text-blue-600 text-center cursor-pointer hover:text-blue-800 hover:underline"
                          onClick={() => handleProgramClick(item.structure, item.module, item.program)}
                        >
                          {item.program}
                        </td>
                        
                        {/* 단기 데이터 */}
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-blue-25">
                          {item.categories?.['단기']?.교육기간 || 0}일
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-blue-25">
                          {item.categories?.['단기']?.교육장소 || 0}개소
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-blue-25">
                          {item.categories?.['단기']?.강의수 || 0}개
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-blue-25">
                          {item.categories?.['단기']?.강사수 || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-blue-25">
                          {item.categories?.['단기']?.교육생수 || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-center bg-blue-25">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            (item.categories?.['단기']?.출석률 || 0) >= 80 ? 'bg-green-100 text-green-800' :
                            (item.categories?.['단기']?.출석률 || 0) >= 60 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {item.categories?.['단기']?.출석률 || 0}%
                          </span>
                        </td>

                        {/* 중기 데이터 */}
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-green-25">
                          {item.categories?.['중기']?.교육기간 || 0}일
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-green-25">
                          {item.categories?.['중기']?.교육장소 || 0}개소
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-green-25">
                          {item.categories?.['중기']?.강의수 || 0}개
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-green-25">
                          {item.categories?.['중기']?.강사수 || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-green-25">
                          {item.categories?.['중기']?.교육생수 || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-center bg-green-25">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            (item.categories?.['중기']?.출석률 || 0) >= 80 ? 'bg-green-100 text-green-800' :
                            (item.categories?.['중기']?.출석률 || 0) >= 60 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {item.categories?.['중기']?.출석률 || 0}%
                          </span>
                        </td>

                        {/* 장기 데이터 */}
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-yellow-25">
                          {item.categories?.['장기']?.교육기간 || 0}일
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-yellow-25">
                          {item.categories?.['장기']?.교육장소 || 0}개소
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-yellow-25">
                          {item.categories?.['장기']?.강의수 || 0}개
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-yellow-25">
                          {item.categories?.['장기']?.강사수 || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-gray-500 text-center bg-yellow-25">
                          {item.categories?.['장기']?.교육생수 || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-3 text-xs text-center bg-yellow-25">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            (item.categories?.['장기']?.출석률 || 0) >= 80 ? 'bg-green-100 text-green-800' :
                            (item.categories?.['장기']?.출석률 || 0) >= 60 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {item.categories?.['장기']?.출석률 || 0}%
                          </span>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>

          {reportData.details?.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">조건에 맞는 데이터가 없습니다.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // 일자별 상세 리포트 렌더링
  const renderDailyReport = () => {
    if (!reportData) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">일자별 출결 상세 리포트</h3>
          <p className="text-sm text-gray-600 mt-1">강의별 상세 출결 현황</p>
        </div>
        
        <div className="p-6">
          {reportData.classes?.map((classItem, classIndex) => (
            <div key={classIndex} className="mb-8 last:mb-0">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{classItem.className}</h4>
                  <p className="text-sm text-gray-600">
                    {classItem.date} | 강사: {classItem.instructorName} | 장소: {classItem.location}
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    출석률: {classItem.attendanceRate?.toFixed(1)}%
                  </span>
                  <CalendarIcon className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        교육생명
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        출결상태
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        인증방식
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        출석시간
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        GPS 거리
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {classItem.students?.map((student, studentIndex) => (
                      <tr key={studentIndex} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {student.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            student.status === '출석' ? 'bg-green-100 text-green-800' :
                            student.status === '지각' ? 'bg-yellow-100 text-yellow-800' :
                            student.status === '조퇴' ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {student.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {student.authMethod}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {student.attendanceTime}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {student.gpsDistance}m
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 개별 이수 리포트 렌더링 (엑셀 표 스타일)
  const renderIndividualReport = () => {
    if (!reportData) return null;

    // 모든 학생의 강의 출결 정보를 한 배열로 펼침
    const rows = [];
    reportData.students?.forEach((student) => {
      student.classDetails?.forEach((classDetail) => {
        // 기수를 숫자로 변환 (예: "1기" → 1)
        let batchNumber = '';
        const batchStr = student.batch || '';
        if (batchStr) {
          const match = batchStr.match(/(\d+)/);
          if (match) {
            batchNumber = match[1];
          }
        }

        // 시간을 hh:mm 포맷으로 변환하는 함수
        const formatTime = (timeStr) => {
          if (!timeStr || timeStr === '-') return '-';
          // HH:mm:ss 형식에서 HH:mm만 추출
          const match = timeStr.match(/(\d{2}):(\d{2})/);
          if (match) {
            return `${match[1]}:${match[2]}`;
          }
          return timeStr;
        };

        rows.push({
          date: classDetail.date,
          studentName: student.name,
          category: classDetail.category || '',
          batch: batchNumber,
          structure: classDetail.structure || '',
          module: classDetail.module || '',
          program: classDetail.program || '',
          className: classDetail.className,
          classroom: classDetail.classroom?.name || '',
          instructor: classDetail.instructorName || '',
          inTime: formatTime(classDetail.checkInTime),
          outTime: formatTime(classDetail.checkOutTime),
          method: classDetail.method || ''
        });
      });
    });

    // 교육생별로 그룹화 후 날짜순 정렬 (엑셀과 동일)
    rows.sort((a, b) => {
      // 먼저 날짜로 정렬
      const dateCompare = new Date(a.date) - new Date(b.date);
      if (dateCompare !== 0) return dateCompare;
      
      // 같은 날짜이면 교육생 이름으로 정렬
      return a.studentName.localeCompare(b.studentName, 'ko');
    });

    // 날짜를 한국어 형식으로 포맷팅하는 함수
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
      const weekday = weekdays[date.getDay()];
      return `${year}.${month}.${day}(${weekday})`;
    };

    console.log(rows);

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">교육생 개별 이수 리포트</h3>
          <p className="text-sm text-gray-600 mt-1">날짜별/교육생별 정렬</p>
        </div>
        <div className="p-6 overflow-x-auto">
          <table className="min-w-full border-collapse border border-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="border px-2 py-2 text-xs sticky left-0 bg-gray-50 z-10">날짜</th>
                <th className="border px-2 py-2 text-xs sticky left-[100px] bg-gray-50 z-10">교육생</th>
                <th className="border px-2 py-2 text-xs">교육구분</th>
                <th className="border px-2 py-2 text-xs">기수</th>
                <th className="border px-2 py-2 text-xs">강의구성</th>
                <th className="border px-2 py-2 text-xs">모듈</th>
                <th className="border px-2 py-2 text-xs">프로그램</th>
                <th className="border px-2 py-2 text-xs">강의명</th>
                <th className="border px-2 py-2 text-xs">교육장소</th>
                <th className="border px-2 py-2 text-xs">강사</th>
                <th className="border px-2 py-2 text-xs">입실시간</th>
                <th className="border px-2 py-2 text-xs">퇴실시간</th>
                <th className="border px-2 py-2 text-xs">출석방법</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={13} className="text-center py-8 text-gray-500">조건에 맞는 데이터가 없습니다.</td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="border px-2 py-2 text-xs text-center sticky left-0 bg-white">
                      {formatDate(row.date)}
                    </td>
                    <td className="border px-2 py-2 text-xs text-center sticky left-[100px] bg-white font-medium">
                      {row.studentName}
                    </td>
                    <td className="border px-2 py-2 text-xs text-center">{row.category}</td>
                    <td className="border px-2 py-2 text-xs text-center">{row.batch}</td>
                    <td className="border px-2 py-2 text-xs text-center">{row.structure}</td>
                    <td className="border px-2 py-2 text-xs text-center">{row.module}</td>
                    <td className="border px-2 py-2 text-xs text-center">{row.program}</td>
                    <td className="border px-2 py-2 text-xs text-center">{row.className}</td>
                    <td className="border px-2 py-2 text-xs text-center">{row.classroom}</td>
                    <td className="border px-2 py-2 text-xs text-center">{row.instructor}</td>
                    <td className="border px-2 py-2 text-xs text-center">{row.inTime}</td>
                    <td className="border px-2 py-2 text-xs text-center">{row.outTime}</td>
                    <td className="border px-2 py-2 text-xs text-center">{row.method}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-full">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">보고서 관리</h1>
        <p className="text-gray-600">출결 현황과 교육 이수 결과를 확인할 수 있습니다.</p>
      </div>

      {/* 탭 네비게이션 */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {reportTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  <div className="text-left">
                    <div>{tab.name}</div>
                    <div className="text-xs text-gray-500">{tab.description}</div>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* 필터 섹션 */}
      {showFilters && renderFilters()}

      {/* 필터 토글 버튼 */}
      <div className="flex justify-center mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          {showFilters ? (
            <>
              <EyeSlashIcon className="w-4 h-4 mr-2" />
              필터 숨기기
              <ChevronUpIcon className="w-4 h-4 ml-2" />
            </>
          ) : (
            <>
              <EyeIcon className="w-4 h-4 mr-2" />
              필터 보기
              <ChevronDownIcon className="w-4 h-4 ml-2" />
            </>
          )}
        </button>
      </div>

      {/* 액션 버튼 */}
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-gray-600">
          {reportData && (
            <>
              {activeTab === 'comprehensive' && `총 ${reportData.details?.length || 0}개 항목`}
              {activeTab === 'daily' && `총 ${reportData.classes?.length || 0}개 강의`}
              {activeTab === 'individual' && `총 ${reportData.students?.length || 0}명 교육생`}
            </>
          )}
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={downloadExcel}
            disabled={isLoading || !reportData}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Excel 다운로드
          </button>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* 로딩 상태 */}
      {isLoading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* 보고서 내용 */}
      {!isLoading && !error && reportData && (
        <>
          {activeTab === 'comprehensive' && renderComprehensiveReport()}
          {activeTab === 'daily' && renderDailyReport()}
          {activeTab === 'individual' && renderIndividualReport()}
        </>
      )}

      {/* 데이터 없음 상태 */}
      {!isLoading && !error && !reportData && (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">보고서 데이터가 없습니다</h3>
          <p className="mt-1 text-sm text-gray-500">
            필터 조건을 설정하고 조회 버튼을 클릭해주세요.
          </p>
        </div>
      )}

      {/* 프로그램별 강의 상세 모달 */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* 모달 헤더 */}
              <div className="flex items-center justify-between pb-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  {modalData ? `${modalData.structure} > ${modalData.module} > ${modalData.program}` : '강의 상세 정보'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* 모달 콘텐츠 */}
              <div className="mt-4">
                {modalLoading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                ) : modalData ? (
                  <div>
                    {/* 요약 정보 */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center">
                          <p className="text-sm text-gray-600">총 강의 수</p>
                          <p className="text-2xl font-bold text-blue-600">{modalData.totalClasses}개</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">구성</p>
                          <p className="text-lg font-semibold text-gray-900">{modalData.structure}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">모듈</p>
                          <p className="text-lg font-semibold text-gray-900">{modalData.module}</p>
                        </div>
                      </div>
                    </div>

                    {/* 강의 목록 테이블 */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              강의명
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              날짜
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              시간
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              장소
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              강사
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              교육구분
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              수강생
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              출석률
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {modalData.classes?.map((classItem, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {classItem.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {classItem.date}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {classItem.startTime} ~ {classItem.endTime}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {classItem.location}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {classItem.instructorName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  classItem.category === '단기' ? 'bg-blue-100 text-blue-800' :
                                  classItem.category === '중기' ? 'bg-green-100 text-green-800' :
                                  classItem.category === '장기' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {classItem.category}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {classItem.attendanceCount}/{classItem.totalStudents}명
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  classItem.attendanceRate >= 80 ? 'bg-green-100 text-green-800' :
                                  classItem.attendanceRate >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {classItem.attendanceRate}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {modalData.classes?.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-gray-500">등록된 강의가 없습니다.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">데이터를 불러올 수 없습니다.</p>
                  </div>
                )}
              </div>

              {/* 모달 푸터 */}
              <div className="flex justify-end pt-4 border-t mt-6">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports; 